import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import '../Style/App.css';
import { API_URL } from '../config/api';

export default function SignUp() {
  const [values, setValues] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    badgeId: '',
    title: ''
  });
  const [error, setError]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const navigate            = useNavigate();

  axios.defaults.withCredentials = true;

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limitedPhoneNumber = phoneNumber.slice(0, 10);

    // Don't format if empty or too short
    if (limitedPhoneNumber.length === 0) {
      return '';
    }

    // Format based on length
    if (limitedPhoneNumber.length <= 3) {
      return limitedPhoneNumber;
    } else if (limitedPhoneNumber.length <= 6) {
      return `${limitedPhoneNumber.slice(0, 3)}-${limitedPhoneNumber.slice(3)}`;
    } else {
      return `${limitedPhoneNumber.slice(0, 3)}-${limitedPhoneNumber.slice(3, 6)}-${limitedPhoneNumber.slice(6)}`;
    }
  };

  // Get clean phone number (digits only)
  const getCleanPhoneNumber = (formattedPhone) => {
    return formattedPhone.replace(/\D/g, '');
  };

  // Validate password requirements
  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(req => req);
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setValues({ ...values, password: newPassword });
    validatePassword(newPassword);
  };

  const handle = async e => {
    e.preventDefault();

    // Validate password requirements
    if (!validatePassword(values.password)) {
      setError('Password does not meet all requirements');
      return;
    }

    // Validate passwords match
    if (values.password !== values.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate phone number is exactly 10 digits
    const cleanPhone = getCleanPhoneNumber(values.phoneNumber);
    if (cleanPhone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      return;
    }

    // Clear any previous errors
    setError('');

    try {
      // Prepare signup data with clean phone number and normalized email
      const { confirmPassword, phoneNumber, ...signupData } = values;
      const normalizedEmail = values.email ? values.email.toLowerCase().trim() : '';
      const dataToSend = {
        ...signupData,
        email: normalizedEmail, // Normalize email for new users
        phoneNumber: cleanPhone // Send clean phone number (digits only)
      };

      await axios.post(
        `${API_URL}/auth/signup`,
        dataToSend,
        { withCredentials: true }
      );
      navigate(`/confirm?email=${encodeURIComponent(normalizedEmail)}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="loginPage">
      <img
        src="/GradyStar.png"
        alt="Logo"
        className="logo"
      />
      <div className="loginForm">
        {error && <div className="text-danger mb-2">{error}</div>}
        <h2 className="text-center"><strong>Sign Up</strong></h2>
        <form onSubmit={handle}>
          <div style={{display: "flex", gap: "15px", marginBottom: "5px"}}>
            <div style={{flex: "1", textAlign: "left"}}>
              <label style={{display: "block", marginBottom: "5px"}}><strong>First Name:</strong></label>
              <input
                type="text"
                className="modern-input"
                value={values.firstName}
                onChange={e => setValues({ ...values, firstName: e.target.value })}
                required
              />
            </div>
            <div style={{flex: "1", textAlign: "left"}}>
              <label style={{display: "block", marginBottom: "5px"}}><strong>Last Name:</strong></label>
              <input
                type="text"
                className="modern-input"
                value={values.lastName}
                onChange={e => setValues({ ...values, lastName: e.target.value })}
                required
              />
            </div>
          </div>
          <div style={{textAlign: "left", marginBottom: "5px"}}>
            <label style={{display: "block", marginBottom: "5px"}}><strong>Email:</strong></label>
            <input
              type="email"
              className="modern-input"
              value={values.email}
              onChange={e => setValues({ ...values, email: e.target.value })}
              required
            />
          </div>
          <div style={{display: "flex", gap: "15px", marginBottom: "5px"}}>
            <div style={{flex: "1", textAlign: "left"}}>
              <label style={{display: "block", marginBottom: "5px"}}><strong>Phone Number:</strong></label>
              <input
                type="tel"
                className="modern-input"
                value={values.phoneNumber}
                onChange={e => setValues({ ...values, phoneNumber: formatPhoneNumber(e.target.value) })}
                placeholder="888-888-8888"
                maxLength="12"
                required
              />
            </div>
            <div style={{flex: "1", textAlign: "left"}}>
              <label style={{display: "block", marginBottom: "5px"}}><strong>Badge/ID:</strong></label>
              <input
                type="text"
                className="modern-input"
                value={values.badgeId}
                onChange={e => setValues({ ...values, badgeId: e.target.value })}
                placeholder="Badge or ID number"
                required
              />
            </div>
          </div>
          <div style={{textAlign: "left", marginBottom: "5px"}}>
            <label style={{display: "block", marginBottom: "5px"}}><strong>Title:</strong></label>
            <input
              type="text"
              className="modern-input"
              value={values.title}
              onChange={e => setValues({ ...values, title: e.target.value })}
              placeholder="Sergeant, Lieutenant, Captain, etc."
              required
            />
          </div>
          <div style={{textAlign: "left", marginBottom: "5px"}}>
            <label style={{display: "block", marginBottom: "5px"}}><strong>Password:</strong></label>
            <div style={{position: "relative"}}>
              <input
                type={showPassword ? "text" : "password"}
                className="modern-input"
                value={values.password}
                onChange={handlePasswordChange}
                required
                style={{paddingRight: "45px"}}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="password-toggle-btn"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#666",
                  padding: "2px 6px",
                  width: "auto",
                  height: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {/* Password Requirements */}
            <div style={{
              marginTop: "8px",
              padding: "10px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "4px",
              fontSize: "12px"
            }}>
              <div style={{fontWeight: "bold", marginBottom: "6px", color: "#495057"}}>
                Password Requirements:
              </div>
              <div style={{display: "flex", flexDirection: "column", gap: "2px"}}>
                <div style={{
                  color: passwordRequirements.length ? "#28a745" : "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>{passwordRequirements.length ? "✓" : "○"}</span>
                  At least 8 characters long
                </div>
                <div style={{
                  color: passwordRequirements.uppercase ? "#28a745" : "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>{passwordRequirements.uppercase ? "✓" : "○"}</span>
                  At least one uppercase letter (A-Z)
                </div>
                <div style={{
                  color: passwordRequirements.lowercase ? "#28a745" : "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>{passwordRequirements.lowercase ? "✓" : "○"}</span>
                  At least one lowercase letter (a-z)
                </div>
                <div style={{
                  color: passwordRequirements.number ? "#28a745" : "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>{passwordRequirements.number ? "✓" : "○"}</span>
                  At least one number (0-9)
                </div>
                <div style={{
                  color: passwordRequirements.special ? "#28a745" : "#6c757d",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}>
                  <span>{passwordRequirements.special ? "✓" : "○"}</span>
                  At least one special character (!@#$%^&*()_+-=[]{}|;':",./{'>'}?)
                </div>
              </div>
            </div>
          </div>
          <div style={{textAlign: "left", marginBottom: "5px"}}>
            <label style={{display: "block", marginBottom: "5px"}}><strong>Confirm Password:</strong></label>
            <div style={{position: "relative"}}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="modern-input"
                value={values.confirmPassword}
                onChange={e => setValues({ ...values, confirmPassword: e.target.value })}
                required
                style={{paddingRight: "45px"}}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="password-toggle-btn"
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#666",
                  padding: "2px 6px",
                  width: "auto",
                  height: "auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                title={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div style={{textAlign: "left", marginBottom: "5px"}}>
            <label style={{display: "block", marginBottom: "5px"}}><strong>Role:</strong></label>
            <select
              className="modern-input"
              value={values.role}
              onChange={e => setValues({ ...values, role: e.target.value })}
              required
              style={{width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px"}}
            >
              <option value="">Select your role</option>
              <option value="Officer">Officer</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Detention">Detention</option>
            </select>
          </div>
          <button className="modern-btn">
            Sign Up
          </button>
          <div style={{marginTop: "10px"}}>
            <p style={{fontSize: "14px", margin: "0"}}>Already have an account? <Link to="/login" style={{color: "#0b3f1f"}}>Log In</Link></p>
          </div>
        </form>
      </div>
    </div>
  );
}
